/* ============================================================
   DashboardTalabarteriaGA.jsx
   Ruta: /caso/talabarteria-ga
   Reemplaza la sección "Qué hicimos · con números"
   Design system Thinker & Doer
   Requiere: chart.js  (npm install chart.js)
   ============================================================ */

import React, { useEffect, useRef } from "react";
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from "chart.js";

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip);

const MESES = ["Oct", "Nov", "Dic", "Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul"];

const ALCANCE_ACC = [
  150122, 256682, 605984, 742036, 877323, 996310, 1291961, 1405067, 1646237, 2049007,
];
const INTERACCIONES_ACC = [
  5103, 9245, 29316, 32619, 36951, 41022, 45549, 47780, 51191, 53866,
];
const POR_RED = { facebook: 890818, tiktok: 815602, instagram: 342587 };

const KPIS = [
  { label: "Alcance total", valor: "2.06M", nota: "desde 0 impresiones", color: "#FDBE02" },
  { label: "Ventas digitales", valor: "+120%", nota: "pedidos por redes sociales", color: "#eb636b" },
  { label: "Interacciones", valor: "53.8K", nota: "de la comunidad", color: "#5dc1b9" },
  { label: "Facebook", valor: "+3,900%", nota: "por publicación", color: "#FDBE02" },
  { label: "Engagement TikTok", valor: "4.3%", nota: "sobre el sector", color: "#1D9E75" },
  { label: "Contenido", valor: "935", nota: "publicaciones", color: "#8b7ff0" },
];

function useBarChart(data, color, horizontal = false, stacked = null) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const ctx = ref.current.getContext("2d");
    Chart.defaults.font.family = "'DM Sans', sans-serif";

    const fmt = (v) =>
      v >= 1000000 ? v / 1000000 + "M" : v >= 1000 ? v / 1000 + "K" : v;

    let config;
    if (stacked) {
      config = {
        type: "bar",
        data: {
          labels: ["Alcance"],
          datasets: [
            { label: "Facebook", data: [POR_RED.facebook], backgroundColor: "#5dc1b9", maxBarThickness: 48 },
            { label: "TikTok", data: [POR_RED.tiktok], backgroundColor: "#FDBE02", maxBarThickness: 48 },
            { label: "Instagram", data: [POR_RED.instagram], backgroundColor: "#eb636b", maxBarThickness: 48 },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: "#10211f",
              borderColor: "rgba(93,193,185,.3)",
              borderWidth: 1,
              callbacks: { label: (c) => c.dataset.label + ": " + c.parsed.x.toLocaleString() },
            },
          },
          scales: {
            x: {
              stacked: true,
              beginAtZero: true,
              grid: { color: "rgba(93,193,185,.10)" },
              border: { display: false },
              ticks: { color: "#6f8683", callback: fmt },
            },
            y: { stacked: true, grid: { display: false }, border: { display: false }, ticks: { display: false } },
          },
        },
      };
    } else {
      config = {
        type: "bar",
        data: {
          labels: MESES,
          datasets: [{ data, backgroundColor: color, borderRadius: 5, maxBarThickness: 15 }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: "#10211f",
              borderColor: "rgba(93,193,185,.3)",
              borderWidth: 1,
              callbacks: { label: (c) => c.parsed.y.toLocaleString() },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: { color: "rgba(93,193,185,.10)" },
              border: { display: false },
              ticks: { color: "#6f8683", font: { size: 10 }, callback: fmt },
            },
            x: { grid: { display: false }, border: { display: false }, ticks: { color: "#6f8683", font: { size: 10 } } },
          },
        },
      };
    }

    const chart = new Chart(ctx, config);
    return () => chart.destroy();
  }, [data, color, horizontal, stacked]);
  return ref;
}

export default function DashboardTalabarteriaGA() {
  const alcanceRef = useBarChart(ALCANCE_ACC, "#5dc1b9");
  const interRef = useBarChart(INTERACCIONES_ACC, "#FDBE02");
  const stackRef = useBarChart(null, null, true, true);

  return (
    <section className="td-dashboard">
      <style>{`
        .td-dashboard{
          font-family:'DM Sans',sans-serif;
          border-radius:22px;
          padding:30px 28px 26px;
          color:#eaf5f3;
          background:
            radial-gradient(1000px 500px at 85% -10%, rgba(31,211,200,.16), transparent 60%),
            radial-gradient(800px 460px at -10% 115%, rgba(235,99,107,.14), transparent 55%),
            #0c1817;
        }
        .td-dashboard .serif{font-family:'DM Serif Display',serif;}
        .td-dashboard .mono{font-family:'JetBrains Mono',monospace;}
        .td-banner{
          display:flex;align-items:center;gap:9px;
          background:rgba(93,193,185,.10);
          border:1px solid rgba(93,193,185,.22);
          border-radius:12px;padding:11px 15px;margin:0 0 22px;
        }
        .td-kpis{
          display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));
          gap:11px;margin-bottom:14px;
        }
        .td-kpi{
          background:rgba(255,255,255,.04);
          border:1px solid rgba(93,193,185,.14);
          border-radius:14px;padding:15px;
        }
        .td-kpi-label{
          margin:0 0 5px;font-family:'JetBrains Mono',monospace;
          font-size:10px;letter-spacing:.09em;text-transform:uppercase;color:#9fb4b1;
        }
        .td-kpi-valor{margin:0;font-family:'DM Serif Display',serif;font-size:27px;color:#fff;}
        .td-kpi-nota{margin:4px 0 0;font-size:11.5px;color:#5dc1b9;}
        .td-charts-2{display:grid;grid-template-columns:1fr 1fr;gap:11px;margin-bottom:14px;}
        .td-card{
          background:rgba(255,255,255,.03);
          border:1px solid rgba(93,193,185,.14);
          border-radius:16px;padding:18px;
        }
        .td-card-title{margin:0 0 2px;font-family:'DM Serif Display',serif;font-size:15px;color:#eaf5f3;}
        .td-card-sub{margin:0 0 13px;font-size:11.5px;color:#9fb4b1;}
        .td-canvas-wrap{position:relative;width:100%;height:175px;}
        .td-canvas-wrap.short{height:105px;}
        .td-legend{display:flex;flex-wrap:wrap;gap:16px;margin-bottom:13px;font-size:11.5px;color:#cfe6e3;}
        .td-legend span{display:flex;align-items:center;gap:6px;}
        .td-dot{width:11px;height:11px;border-radius:3px;}
        .td-foot{
          margin:20px 0 0;font-family:'JetBrains Mono',monospace;
          font-size:11px;letter-spacing:.06em;color:#6f8683;text-align:center;
        }
        @media(max-width:640px){ .td-charts-2{grid-template-columns:1fr;} }
      `}</style>

      <div className="td-banner">
        <span aria-hidden="true">🚀</span>
        <p style={{ margin: 0, fontSize: "13.5px", color: "#cfe6e3" }}>
          <strong style={{ color: "#fff" }}>Empezamos desde cero.</strong> Creamos sus tres redes
          (Facebook, Instagram y TikTok) sin seguidores ni contenido previo. Esto es lo que se
          construyó en 10 meses.
        </p>
      </div>

      <div className="td-kpis">
        {KPIS.map((k) => (
          <div className="td-kpi" key={k.label}>
            <p className="td-kpi-label">{k.label}</p>
            <p className="td-kpi-valor" style={{ color: k.color }}>{k.valor}</p>
            <p className="td-kpi-nota">↗ {k.nota}</p>
          </div>
        ))}
      </div>

      <div className="td-charts-2">
        <div className="td-card">
          <p className="td-card-title">Alcance acumulado</p>
          <p className="td-card-sub">de 0 a 2.05M en 10 meses</p>
          <div className="td-canvas-wrap">
            <canvas ref={alcanceRef} role="img" aria-label="Alcance acumulado de cero a 2 millones." />
          </div>
        </div>
        <div className="td-card">
          <p className="td-card-title">Interacciones acumuladas</p>
          <p className="td-card-sub">comunidad creciendo mes a mes</p>
          <div className="td-canvas-wrap">
            <canvas ref={interRef} role="img" aria-label="Interacciones acumuladas hasta 53.8 mil." />
          </div>
        </div>
      </div>

      <div className="td-card">
        <p className="td-card-title">Alcance total por red</p>
        <p className="td-card-sub">2.05M impresiones acumuladas</p>
        <div className="td-legend">
          <span><span className="td-dot" style={{ background: "#5dc1b9" }} />Facebook · 891K</span>
          <span><span className="td-dot" style={{ background: "#FDBE02" }} />TikTok · 816K</span>
          <span><span className="td-dot" style={{ background: "#eb636b" }} />Instagram · 343K</span>
        </div>
        <div className="td-canvas-wrap short">
          <canvas ref={stackRef} role="img" aria-label="Barra apilada: Facebook 891 mil, TikTok 816 mil, Instagram 343 mil." />
        </div>
      </div>

      <p className="td-foot">Marcas reales en lugares reales · Onsite Growth México</p>
    </section>
  );
}
